import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { BomTableIdentifier, IBOMItem, IBomModel } from '../../../prisma-xs/models/bom.model';
import { IProduct, ProductStatus } from '../../../shared/models/product.model';
import { IProductData } from '../../../enclosure/services/device.service';
import { AutocompleteFieldsConstants } from '../../constants/autocomplete-fields.constants';
import { findArticle } from '../../../enclosure/helpers/accessories.helper';
import { TableHeaderNamesConstants } from '../../constants/table-header-names.constants';
import {
  BOMDeliveryKindUpdate,
} from '../../../enclosure/models/product-accessories.model';
import { DeviceImageConstants } from '../../constants/device-image.constants';
import { SortingOrder, TableSortDetails } from '../../../shared/models/table-sort.model';
import { ISearchData } from '../../../enclosure/models/search-data.model';
import { AddReferenceEventEnum, AddReferenceFromBOMCommand } from '../../../shared/commands/AddReferenceFromBom';
import { HighlightOnChangeComponent } from '../highlight-on-change/highlight-on-change.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-ui-bom-table',
    templateUrl: './bom-table.component.html',
    styleUrls: ['./bom-table.component.scss'],
    standalone: false
})
export class BomTableComponent {

  activeStatus = ProductStatus.ACTIVE;
  headerNames = TableHeaderNamesConstants;
  deviceImages = DeviceImageConstants;
  tableIdentifiers = BomTableIdentifier;
  tableSortSequence = SortingOrder;

  /// @TODO remove this and move this to child components
  autoEnumberValue: string;
  autoReferenceValue: string;
  accessoriesDeliveryDirectRadio = true;

  @ViewChild('autoReference') autoReference;
  @ViewChild('autoEnumber') autoEnumber;
  @ViewChild('descriptionInput') descriptionInput: ElementRef;
  @ViewChild('unitPriceInput') unitPriceInput: ElementRef;
  @ViewChild('newAccessoriesProductSelected') newAccessoriesProductSelected: ElementRef;

  @Input() selectedItems: number[];
  @Input() displayTotalPrice = false;
  @Input() bomModels: IBomModel[];
  @Input() discount: number;
  @Input() allowAdding: boolean;
  @Input() isAccessoriesModel: boolean;
  @Input() isProjectComponentsModel: boolean;
  @Input() discountEnabled: boolean;
  @Input() deliveryDisabled: boolean;
  @Input() quantityChangeable: boolean;
  @Input() bomItem: IBOMItem;
  @Input() priceSummaryEnabled = false;
  @Input() priceSummary: string;
  @Input() currency: string;
  @Input() displayPrice: boolean;
  @Input() priceWithoutVatTotal: number;
  @Input() productsWithLimit: IProduct[];
  @Input() keywordReference: string;
  @Input() keywordEnumber: string;
  @Input() tableIdentifier: BomTableIdentifier;
  @Input() selectedApartments: number[];
  @Input() isAddButtonVisible: boolean;
  @Input() quantity = 1;
  @Input() referenceFieldInvalid: boolean;
  @Input() enumberFieldInvalid: boolean;
  @Input() isEnumberFieldStartWithIncorrectNumber: boolean;
  @Input() isEditableProject: boolean;
  @Input() sortSelection: TableSortDetails;
  @Input() set clearInputFields(value) {
    if(value === true) {
      this.clearAndCloseAfterAdding();
    }
  }
  @Input()  increaseTopStickyPosition = false;
  @Input()  currentLanguage : string;

  @Output() quantityDecrease = new EventEmitter<IBomModel>();
  @Output() quantityIncrease = new EventEmitter<IBomModel>();
  @Output() productSearch = new EventEmitter<ISearchData>();
  @Output() closeAutocomplete = new EventEmitter();
  @Output() deleteBomItem = new EventEmitter<IBomModel>();
  @Output() addAccessories = new EventEmitter<AddReferenceFromBOMCommand>();
  @Output() toggleModel = new EventEmitter<IBomModel>();
  @Output() deliveryOptions = new EventEmitter<BOMDeliveryKindUpdate>();
  @Output() customProduct = new EventEmitter<IProductData>();
  @Output() sortDevice = new EventEmitter<TableHeaderNamesConstants>();
  @Output() changeButtonVisibilityForCustomProducts = new EventEmitter<boolean>();
  @Output() downloadDataSheetTrigger = new EventEmitter<string>();

  onSortDevices(parameter: TableHeaderNamesConstants) {
    this.sortDevice.emit(parameter);
  }

  deliveryChanged(bomModel: IBomModel, isDirectDelivery: boolean, tableIdentifier: BomTableIdentifier) {
    this.deliveryOptions.emit({bomModel, isDirectDelivery, tableIdentifier});
  }

  onSearchProductsByReference(searchString: string) {
    this.autoReferenceValue = searchString;
    this.productSearch.emit({searchString, keyword: AutocompleteFieldsConstants.REFERENCE});
  }

  onSearchProductsByEnumber(searchString: string) {
    this.autoEnumberValue = searchString;
    this.productSearch.emit({searchString: searchString.toUpperCase(), keyword: AutocompleteFieldsConstants.ENUMBER});
  }

  onDeleteBomItem(bomItem: IBomModel) {
    this.deleteBomItem.emit(bomItem);
  }

  toggleSelection(model: IBomModel) {
    this.toggleModel.emit(model);
  }

  onClosedAutocomplete() {
    this.closeAutocomplete.emit();
  }

  onInputClearedEnumber() {
    this.autoEnumberValue = '';
    this.enumberFieldInvalid = false;
    this.isEnumberFieldStartWithIncorrectNumber = false;
    this.setAddButtonHiddenIfAutocompleteFieldsEmpty();
  }

  onInputClearedReference() {
    this.autoReferenceValue = '';
    this.referenceFieldInvalid = false;
    this.setAddButtonHiddenIfAutocompleteFieldsEmpty();
  }

  clearAndCloseAfterAdding() {
    this.autoEnumber.clear();
    this.autoReference.clear();
    this.autoEnumber.close();
    this.autoReference.close();
    this.autoEnumberValue = '';
    this.autoReferenceValue = '';
    this.descriptionInput.nativeElement.value = '';
    this.unitPriceInput.nativeElement.value = '';
    this.newAccessoriesProductSelected.nativeElement.checked = true;
    this.quantity = 1;
    this.accessoriesDeliveryDirectRadio = true;
  }

  addCustomProduct() {
    const customProductData = this.createDataForCustomProduct();
    customProductData.isSelected = this.newAccessoriesProductSelected.nativeElement.checked;
    this.customProduct.emit(customProductData);

    this.setAddButtonVisible(false);
    this.clearAndCloseAfterAdding();
  }

  onReferenceAdd(item: IProduct) {
    this.addAccessories.emit(new AddReferenceFromBOMCommand(item, AddReferenceEventEnum.selected));
    this.clearAndCloseAfterAdding();
  }

  addProductOnFocusOut(field: string) {
    if (!this.autoReferenceValue && !this.autoEnumberValue) {
      return this.setAddButtonVisible(false);
    }

    if(!this.validateAutocompleteFields(field, this.autoReferenceValue, this.autoEnumberValue)) {
      const value = field === AutocompleteFieldsConstants.REFERENCE ? this.autoReferenceValue : this.autoEnumberValue.toUpperCase();
      this.addAccessories.emit(new AddReferenceFromBOMCommand(field, AddReferenceEventEnum.focusOut, value));
    }
  }

  addProductOnKeyupEnter(field: string) {
    const article = findArticle(field, this.productsWithLimit, this.autoReferenceValue, this.autoEnumberValue);
    if (article) {
      this.onReferenceAdd(article);
    }
  }

  customProductQuantityDecrease() {
    if (this.quantity > 1) {
      this.quantity = this.quantity - 1;
    }
  }

  customProductQuantityIncrease() {
    this.quantity = this.quantity + 1;
  }

  setAddButtonVisible(isVisible: boolean) {
    this.changeButtonVisibilityForCustomProducts.emit(isVisible);
  }

  setAddButtonHiddenIfAutocompleteFieldsEmpty() {
    if (!this.autoReferenceValue && !this.autoEnumberValue) {
      this.setAddButtonVisible(false);
    }
  }

  createDataForCustomProduct() {
    const productData: IProductData = {
      description: this.descriptionInput.nativeElement.value,
      quantity: this.quantity,
      deliverToCustomer: this.accessoriesDeliveryDirectRadio,
      deliverToPanelBuilder: !this.accessoriesDeliveryDirectRadio
    };
    if (this.unitPriceInput.nativeElement.value > 0) {
      productData.price = Number(this.unitPriceInput.nativeElement.value);
    }
    if (this.autoReferenceValue) {
      productData.reference = this.autoReferenceValue;
    }
    if (this.autoEnumberValue) {
      productData.enumber = this.autoEnumberValue;
    }

    return productData;
  }

  validateAutocompleteFields(field: string, referenceValue: string, enumberValue: string) {
    switch (field) {
      case AutocompleteFieldsConstants.REFERENCE: {
        return this.validateReferenceField(referenceValue);
      }
      case AutocompleteFieldsConstants.ENUMBER: {
        return this.validateEnumberField(enumberValue) && this.validateEnumberFieldIncorrectStart(enumberValue);
      }
    }
  }

  validateReferenceField(value: string) {
    return this.referenceFieldInvalid = value ? (value.length < 2 || value.length > 20) : false;
  }

  validateEnumberField(value: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.enumberFieldInvalid = value ? !(value.length === 9 && !isNaN(value as any)) : false;
  }

  validateEnumberFieldIncorrectStart(value: string) {
    return this.isEnumberFieldStartWithIncorrectNumber = value.startsWith('0');
  }

  onDecreaseQuantity(quantity: number, bomItem: IBomModel) {
    this.quantityDecrease.emit({ ...bomItem, quantity});
  }

  onIncreaseQuantity(quantity: number, bomItem: IBomModel) {
    this.quantityIncrease.emit({ ...bomItem, quantity});
  }

  onQuantityChange(tr: HTMLTableRowElement) {
    HighlightOnChangeComponent.addAndRemoveTransitionClass(tr);
  }

  trackByIndex(_, bom: IBomModel) {
    return bom?.article?.ref;
  }

  toggleRadioSelectionForDeliveryInNewAccessory(isDirectDelivery: boolean) {
    if(this.accessoriesDeliveryDirectRadio !== isDirectDelivery) {
      this.accessoriesDeliveryDirectRadio = isDirectDelivery;
    }
  }

  downloadDataSheet(reference: string) {
    this.downloadDataSheetTrigger.emit(reference);
  }
}
